import React, { useCallback } from "react";
import { useDispatch, useSelector } from "react-redux";
import axios from "axios";
import SearchAIPrompt from "Components/SearchAI";
import { playAudio } from "Containers/AudioPlayer/AudioPlayerActions";
import {
  setHideSearchAIPrompt,
  setSearchInputValue,
  setShowSearchResults,
  setSearchLoading,
  setSearchStops,
} from "./actions";
import { MEDIA_TYPES, LOCATION_MODULES } from "../../constants";

interface SearchProps {
  locId: string | number;
  tourId: string | number;
  mapObj: any;
}

const SearchAIPromptContainer = ({ locId, tourId, mapObj }: SearchProps) => {
  const dispatch = useDispatch();
  const { showResults, searchInputValue, searchAIStops } = useSelector(
    (state: any) => state.searchAI
  );
  const { audio, progress } = useSelector((state: any) => state.audioPlayer);
  const mapStops = useSelector((state: any) => state.stops);

  const handleOnClose = useCallback(() => {
    dispatch(setHideSearchAIPrompt());
    if (searchAIStops) {
      dispatch(setSearchInputValue(""));
    }
    if (audio && progress !== 0) {
      audio.play();
      dispatch(playAudio());
    }
  }, [dispatch, audio, progress, searchAIStops]);

  const handleInputClick = useCallback((e) => {
    e.stopPropagation();
  }, []);

  const handleInputChange = useCallback(
    (e) => {
      dispatch(setSearchInputValue(e.target.value));
    },
    [dispatch]
  );

  const handleClear = useCallback(
    (e) => {
      e.stopPropagation();
      dispatch(setSearchInputValue(""));
    },
    [dispatch]
  );

  const handleSearchClick = useCallback(
    (e, searchValue: string) => {
      e.stopPropagation();
      if (searchValue) {
        dispatch(setShowSearchResults());
        dispatch(setSearchLoading(true));

        axios
          .get(
            `${process.env.REACT_APP_API_BASE_URL}api/v2/experience/${locId}/tour/${tourId}/ai-search/media`,
            {
              params: {
                q: searchValue,
                page: 0,
                size: 10,
                module_id: LOCATION_MODULES.FULL_MAP,
              },
            }
          )
          .then((response) => {
            const { requestId } = response.data;
            const stops = response.data.resources.data;
            const stopsWithMedia = stops.map((stop: any) => {
              let currentMediaType = stop.media_type;
              const matchedDestination = mapStops.find(
                (dest: any) => dest.stopid === stop.stop_id
              );
              let matchedMedia;
              if (currentMediaType === MEDIA_TYPES.STOP_DESCRIPTION) {
                matchedMedia = matchedDestination?.thumbnail;
              } else {
                if (currentMediaType === MEDIA_TYPES.PANO)
                  currentMediaType = MEDIA_TYPES.PANORAMA; // expects pano to be panorama
                const mediaList =
                  matchedDestination &&
                  matchedDestination[`${currentMediaType}s`];
                matchedMedia = mediaList?.find(
                  (media: any) => media.id === stop.media_id
                );
              }
              return {
                ...stop,
                destination: matchedDestination,
                media: matchedMedia,
                requestId,
              };
            });

            dispatch(setSearchLoading(false));
            dispatch(setSearchStops(stopsWithMedia));
          });
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [dispatch]
  );

  const handleEnterPress = (e) => {
    if (e.key === "Enter") {
      handleSearchClick(e, searchInputValue);
    }
  };

  return (
    <SearchAIPrompt
      onClick={handleOnClose}
      onInputClick={handleInputClick}
      onSearchClick={handleSearchClick}
      onChange={handleInputChange}
      onKeyDown={handleEnterPress}
      onClear={handleClear}
      showResults={showResults}
      searchInputValue={searchInputValue}
      mapObj={mapObj}
    />
  );
};

export default SearchAIPromptContainer;
